import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { instantAtFacilityTime } from "@alm/domain";
import { useState } from "react";
import { MonthGrid } from "@/components/app/calendar/month-grid";
import { monthRange, weekRange, ymd } from "@/components/app/calendar/range";
import { WeekGrid } from "@/components/app/calendar/week-grid";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCalendarAnchor, useCalendarView } from "@/hooks/use-calendar-anchor";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  optionalDay,
  optionalInt,
  optionalTrue,
  stringParams,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { useCan, useFeature } from "@/lib/policy";
import { useZone, useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { ActivityCancelDialog } from "./-components/activity-cancel-dialog";
import { ActivityChip } from "./-components/activity-chip";
import { ActivityDialog } from "./-components/activity-dialog";
import {
  activityFilterInput,
  ActivityFiltersBar,
  type ActivityFilters,
} from "./-components/activity-filters";
import { ANY } from "./-components/filter-choice";
import { ActivityLine } from "./-components/activity-line";
import { ActivityList } from "./-components/activity-list";
import {
  ACTIVITY_VIEW_KEY,
  ACTIVITY_VIEWS,
  dayIn,
  type ActivityRow,
  type ActivityView,
} from "./-components/activity-model";
import { ActivityOutcomeDialog } from "./-components/activity-outcome-dialog";
import { CalendarActions } from "./-components/calendar-actions";
import { CalendarLegend } from "./-components/calendar-legend";
import { EventLayerLine } from "./-components/event-layer-line";
import { PrintDialog, type PrintJob } from "./-components/print-dialog";
import { ShareDialog } from "./-components/share-dialog";
import { useEventLayer, type CalendarItem } from "./-components/use-event-layer";

type ActivitySearch = {
  view?: ActivityView;
  q?: string;
  category?: string;
  hostKind?: string;
  status?: string;
  events?: true;
  on?: string;
  page?: number;
  size?: number;
};

export const Route = createFileRoute("/_authenticated/_care/activity-calendar/")({
  validateSearch: (search: Record<string, unknown>): ActivitySearch => ({
    view: ACTIVITY_VIEWS.find((view) => view === search.view),
    q: optionalSearch(search.q),
    ...stringParams(["category", "hostKind", "status"], search),
    events: optionalTrue(search.events),
    on: optionalDay(search.on),
    page: optionalInt(search.page, 2, 100000),
    size: optionalInt(search.size, 5, 200),
  }),
  component: ActivityCalendarPage,
});

/** The last instant of a calendar day on the building's clock. */
const endOfDayIn = (day: string, zone: string) =>
  new Date(instantAtFacilityTime(day, "23:59", zone).getTime() + 59_999);

/** What the building puts on for its residents: a month, a week, a list; printed, shared,
 * planned. */
function ActivityCalendarPage() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ActivitySearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const zoneFor = useZoneFor();
  const zone = useZone(facilityId);
  const oneBuilding = facilityId !== "all";
  // At the building on screen: a role that plans activities at another building writes nothing
  // here.
  const canWrite = useCan("activity:manage");
  const canReadEvents = useCan("crm:read", oneBuilding ? facilityId : null);
  const eventsOn = useFeature("crm-events", oneBuilding ? facilityId : null);
  const { view, setView } = useCalendarView(
    ACTIVITY_VIEW_KEY,
    ACTIVITY_VIEWS,
    params.view,
    (next) => patch({ view: next }),
  );
  const { monthStart, weekStart, setMonthStart, setWeekStart } = useCalendarAnchor(
    params.on,
    (on) => patch({ on }),
  );
  const { text: searchText, change: setSearchText } = useSearchParamText(params.q, (q) =>
    patch({ q, page: undefined }),
  );
  const filters: ActivityFilters = {
    search: searchText,
    category: params.category ?? ANY,
    hostKind: params.hostKind ?? ANY,
    status: params.status ?? ANY,
    showEvents: Boolean(params.events),
  };
  const page = params.page ?? 1;
  const pageSize = params.size ?? 25;
  const [creating, setCreating] = useState<{ day?: string } | null>(null);
  const [completing, setCompleting] = useState<ActivityRow | null>(null);
  const [cancelling, setCancelling] = useState<ActivityRow | null>(null);
  const [printing, setPrinting] = useState<PrintJob | null>(null);
  const [sharing, setSharing] = useState(false);

  const common = activityFilterInput({ ...filters, search: params.q ?? "" }, facilityId);
  const range = view === "week" ? weekRange(weekStart) : monthRange(monthStart);
  const calendar = useQuery({
    ...orpc.care.activityCalendar.calendar.queryOptions({
      input: { ...common, ...range },
    }),
    enabled: view !== "list",
    placeholderData: keepWhileSame({ facilityId }),
  });
  const list = useQuery({
    ...orpc.care.activityCalendar.list.queryOptions({
      input: {
        ...common,
        from: instantAtFacilityTime(range.from, "00:00", zone),
        to: endOfDayIn(range.to, zone),
        dir: "asc",
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    }),
    enabled: view === "list",
    placeholderData: keepWhileSame({ facilityId }),
  });
  const events = useEventLayer({
    facilityId,
    range,
    enabled: filters.showEvents && canReadEvents && eventsOn && view !== "list",
  });
  const source = view === "list" ? list : calendar;
  const rows: ActivityRow[] = source.data?.rows ?? [];
  const items: CalendarItem[] = [
    ...rows.map((activity) => ({
      kind: "activity" as const,
      id: activity.id,
      activity,
    })),
    ...events,
  ];
  const dayKey = (item: CalendarItem) =>
    item.kind === "activity"
      ? dayIn(item.activity.startsAt, zoneFor(item.activity.facilityId))
      : dayIn(item.event.startsAt, zoneFor(item.event.facilityId));
  const showDay = (day: Date) => {
    patch({ on: ymd(day), view: "week" });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Activity calendar"
        meta="Bingo, chair yoga, the Tuesday outing: what the building puts on, who runs it, who came."
        actions={
          <CalendarActions
            facilityId={facilityId}
            oneBuilding={oneBuilding}
            canWrite={canWrite}
            monthStart={monthStart}
            weekStart={weekStart}
            onShare={() => setSharing(true)}
            onPrint={setPrinting}
            onPlan={() => setCreating({})}
          />
        }
      />
      <Tabs value={view} onValueChange={(next) => setView(next as ActivityView)}>
        <TabsList>
          <TabsTrigger value="month">Month</TabsTrigger>
          <TabsTrigger value="week">Week</TabsTrigger>
          <TabsTrigger value="list">List</TabsTrigger>
        </TabsList>
      </Tabs>
      <ActivityFiltersBar
        filters={filters}
        onChange={(next) => {
          if (next.search !== filters.search) setSearchText(next.search);
          patch({
            category: next.category === ANY ? undefined : next.category,
            hostKind: next.hostKind === ANY ? undefined : next.hostKind,
            status: next.status === ANY ? undefined : next.status,
            events: next.showEvents ? true : undefined,
            page: undefined,
          });
        }}
        eventsAvailable={canReadEvents && eventsOn}
      />
      {view === "list" ? null : <CalendarLegend rows={rows} showEvents={events.length > 0} />}
      {!oneBuilding && view !== "list" ? (
        <p className="text-muted-foreground text-xs">
          Pick one building at the top to print or share its calendar.
        </p>
      ) : null}

      {source.isError ? (
        <ReadFailed
          message="Could not load the activities."
          error={source.error}
          onRetry={() => void source.refetch()}
          retrying={source.isFetching}
        />
      ) : view === "month" ? (
        <MonthGrid
          monthStart={monthStart}
          setMonthStart={setMonthStart}
          items={items}
          dayKey={dayKey}
          renderLine={(item) =>
            item.kind === "activity" ? (
              <ActivityLine activity={item.activity} zone={zoneFor(item.activity.facilityId)} />
            ) : (
              <EventLayerLine event={item.event} zone={zoneFor(item.event.facilityId)} />
            )
          }
          pending={calendar.isPending}
          emptyTitle="No activities this month"
          onPickDay={showDay}
        />
      ) : view === "week" ? (
        <WeekGrid
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          items={items}
          dayKey={dayKey}
          renderChip={(item) =>
            item.kind === "activity" ? (
              <ActivityChip activity={item.activity} zone={zoneFor(item.activity.facilityId)} />
            ) : (
              <EventLayerLine event={item.event} zone={zoneFor(item.event.facilityId)} chip />
            )
          }
          pending={calendar.isPending}
          emptyTitle="No activities this week"
          onPickDay={canWrite ? (day) => setCreating({ day: ymd(day) }) : undefined}
        />
      ) : (
        <ActivityList
          rows={rows}
          pending={list.isPending}
          canWrite={canWrite}
          onComplete={setCompleting}
          onCancel={setCancelling}
          total={list.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPaging={(next) =>
            patch({
              size: next.pageSize === 25 ? undefined : next.pageSize,
              page: next.page > 1 ? next.page : undefined,
            })
          }
        />
      )}

      {creating ? (
        <ActivityDialog
          activity={null}
          defaults={{ day: creating.day, facilityId }}
          onClose={() => setCreating(null)}
        />
      ) : null}
      {completing ? (
        <ActivityOutcomeDialog activity={completing} onClose={() => setCompleting(null)} />
      ) : null}
      {cancelling ? (
        <ActivityCancelDialog activity={cancelling} onClose={() => setCancelling(null)} />
      ) : null}
      {printing ? <PrintDialog job={printing} onClose={() => setPrinting(null)} /> : null}
      {sharing && oneBuilding ? (
        <ShareDialog
          facilityId={facilityId}
          canWrite={canWrite}
          onClose={() => setSharing(false)}
        />
      ) : null}
    </div>
  );
}
